import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined, LocalFireDepartmentOutlined,
    CheckCircleOutline, EmojiEventsOutlined, LockClockOutlined,
} from '@mui/icons-material';
import Confetti from 'react-confetti';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    blue:    '#007AFF',
    gold:    '#F5C842',
    orange:  '#FF9500',
};

const STREAK_REWARDS = [
    { day: 1, reward: '₦50',   label: 'Day 1' },
    { day: 2, reward: '₦50',   label: 'Day 2' },
    { day: 3, reward: '₦75',   label: 'Day 3' },
    { day: 4, reward: '₦75',   label: 'Day 4' },
    { day: 5, reward: '₦100',  label: 'Day 5' },
    { day: 6, reward: '₦100',  label: 'Day 6' },
    { day: 7, reward: '₦200',  label: 'Day 7 🎉' },
];

const HISTORY = [
    { label: 'Yesterday',     amount: '₦50',  status: 'claimed' },
    { label: '2 days ago',    amount: '₦50',  status: 'claimed' },
    { label: '3 days ago',    amount: '₦75',  status: 'claimed' },
    { label: '5 days ago',    amount: '—',    status: 'missed'  },
];

const DailyCheckIn: React.FC = () => {
    const navigate  = useNavigate();
    const theme     = useTheme();
    const isDark    = theme.palette.mode === 'dark';

    const [claimed,   setClaimed]   = useState(false);
    const [streak,    setStreak]    = useState(3);
    const [confetti,  setConfetti]  = useState(false);
    const [countdown, setCountdown] = useState('');

    // Countdown to midnight
    useEffect(() => {
        const tick = () => {
            const now  = new Date();
            const next = new Date();
            next.setHours(24, 0, 0, 0);
            const diff = next.getTime() - now.getTime();
            const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
            const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
            const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
            setCountdown(`${h}:${m}:${s}`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    const handleClaim = () => {
        setClaimed(true);
        setStreak(s => s + 1);
        setConfetti(true);
        setTimeout(() => setConfetti(false), 3500);
    };

    const todayReward  = STREAK_REWARDS[streak] ?? STREAK_REWARDS[6];
    const nextMilestone = streak < 7 ? STREAK_REWARDS[Math.min(streak, 6)] : null;

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
            {confetti && <Confetti recycle={false} numberOfPieces={320} gravity={0.18} />}

            {/* ══ HERO ══ */}
            <Box sx={{
                background: 'linear-gradient(145deg, #003A8C 0%, #0055CC 60%, #007AFF 100%)',
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#fff', .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.blue, .3)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate('/rewards')}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .45), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>Rewards</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Daily Check-In
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .6, background: alpha('#fff', .12), border: `1px solid ${alpha('#fff', .2)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <LocalFireDepartmentOutlined sx={{ color: B.orange, fontSize: 14 }} />
                        <Typography sx={{ color: '#fff', fontSize: '.72rem', fontWeight: 700 }}>{streak} Day Streak</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Claim card ── */}
                <Box sx={{
                    ...glassCard, p: 3, mb: 2, textAlign: 'center', position: 'relative', overflow: 'hidden',
                }}>
                    <Box sx={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.blue, isDark ? .15 : .07)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                    {claimed ? (
                        <>
                            <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                                <CheckCircleOutline sx={{ color: B.green, fontSize: 36 }} />
                            </Box>
                            <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.5rem', mb: .5 }}>
                                Claimed! 🎉
                            </Typography>
                            <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>+{todayReward.reward}</Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 2 }}>Added to your wallet</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75, background: alpha(B.blue, .08), border: `1px solid ${alpha(B.blue, .2)}`, borderRadius: '12px', px: 2, py: 1.25 }}>
                                <LockClockOutlined sx={{ color: B.blue, fontSize: 15 }} />
                                <Typography sx={{ color: B.blue, fontSize: '.75rem', fontWeight: 600 }}>Next reward in {countdown}</Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            {/* Gift icon */}
                            <Box sx={{ width: 80, height: 80, borderRadius: '24px', background: `linear-gradient(145deg, ${alpha(B.blue, .15)}, ${alpha(B.blue, .05)})`, border: `1.5px solid ${alpha(B.blue, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                                <Typography sx={{ fontSize: '2.2rem' }}>🎁</Typography>
                            </Box>
                            <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.4rem', mb: .5 }}>
                                Today's Reward
                            </Typography>
                            <Typography sx={{ color: B.blue, fontWeight: 800, fontSize: '2rem', fontFamily: "'Instrument Serif', serif", mb: .5 }}>
                                {todayReward.reward}
                            </Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 3 }}>
                                Day {streak + 1} bonus — claim before midnight
                            </Typography>
                            <Button fullWidth onClick={handleClaim}
                                    sx={{
                                        py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                                        background: 'linear-gradient(135deg, #0055CC, #007AFF)',
                                        color: '#fff', boxShadow: `0 12px 32px ${alpha(B.blue, .4)}`,
                                        '&:hover': { boxShadow: `0 16px 40px ${alpha(B.blue, .55)}` },
                                    }}>
                                Claim ₦{todayReward.reward.replace('₦', '')}
                            </Button>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75, mt: 2 }}>
                                <LockClockOutlined sx={{ color: textFaint, fontSize: 13 }} />
                                <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>Resets in {countdown}</Typography>
                            </Box>
                        </>
                    )}
                </Box>

                {/* ── 7-day streak tracker ── */}
                <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em' }}>Weekly Streak</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.orange, .1), border: `1px solid ${alpha(B.orange, .25)}`, borderRadius: 20, px: 1.25, py: .35 }}>
                            <LocalFireDepartmentOutlined sx={{ color: B.orange, fontSize: 13 }} />
                            <Typography sx={{ color: B.orange, fontSize: '.7rem', fontWeight: 700 }}>{streak}/7</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: .75 }}>
                        {STREAK_REWARDS.map((d, i) => {
                            const isComplete = i < streak;
                            const isToday    = i === streak;
                            const isFuture   = i > streak;
                            return (
                                <Box key={d.day} sx={{ flex: 1, textAlign: 'center' }}>
                                    <Box sx={{
                                        width: '100%', aspectRatio: '1', borderRadius: '12px', mb: .75,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                                        background: isComplete
                                            ? `linear-gradient(135deg, ${B.blue}, #5AC8FA)`
                                            : isToday
                                                ? alpha(B.blue, .15)
                                                : alpha(isDark ? '#fff' : B.dark, .05),
                                        border: `1.5px solid ${isComplete ? 'transparent' : isToday ? alpha(B.blue, .4) : dividerC}`,
                                        boxShadow: isComplete ? `0 4px 14px ${alpha(B.blue, .3)}` : 'none',
                                        transition: 'all .2s',
                                    }}>
                                        {isComplete
                                            ? <Typography sx={{ fontSize: '.85rem' }}>✓</Typography>
                                            : isToday
                                                ? <Typography sx={{ fontSize: '.85rem' }}>🎁</Typography>
                                                : <Typography sx={{ color: textFaint, fontSize: '.68rem', fontWeight: 700 }}>{d.day}</Typography>
                                        }
                                    </Box>
                                    <Typography sx={{ color: isComplete ? B.blue : isToday ? textPri : textFaint, fontSize: '.58rem', fontWeight: 700, lineHeight: 1.2 }}>
                                        {d.reward}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* 7-day bonus note */}
                    <Box sx={{ mt: 2.5, p: 1.5, borderRadius: '12px', background: alpha(B.gold, .08), border: `1px solid ${alpha(B.gold, .2)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmojiEventsOutlined sx={{ color: B.gold, fontSize: 16, flexShrink: 0 }} />
                        <Typography sx={{ color: alpha(isDark ? '#fff' : B.ink, .6), fontSize: '.72rem', lineHeight: 1.4 }}>
                            Complete all 7 days for a <span style={{ color: B.gold, fontWeight: 700 }}>₦200 bonus</span> — 4 days to go!
                        </Typography>
                    </Box>
                </Box>

                {/* ── Claim history ── */}
                <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>Recent Claims</Typography>
                    </Box>
                    {HISTORY.map((h, i) => (
                        <React.Fragment key={i}>
                            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: h.status === 'claimed' ? B.greenDim : alpha('#FF4D6A', .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography sx={{ fontSize: '.9rem' }}>{h.status === 'claimed' ? '✅' : '❌'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.83rem' }}>{h.label}</Typography>
                                        <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>{h.status === 'claimed' ? 'Reward claimed' : 'Streak broken'}</Typography>
                                    </Box>
                                </Box>
                                <Typography sx={{ color: h.status === 'claimed' ? B.green : '#FF4D6A', fontWeight: 700, fontSize: '.85rem', fontVariantNumeric: 'tabular-nums' }}>
                                    {h.status === 'claimed' ? `+${h.amount}` : h.amount}
                                </Typography>
                            </Box>
                            {i < HISTORY.length - 1 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                        </React.Fragment>
                    ))}
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default DailyCheckIn;